export const objectToFormData = (obj, form, namespace) => {
    const fd = form || new FormData();
    let formKey;

    for (const property in obj) {
        if (obj.hasOwnProperty(property)) {
            if (namespace) {
                formKey = `${namespace}[${property}]`;
            } else {
                formKey = property;
            }

            if (obj[property] instanceof Date) {
                fd.append(formKey, obj[property].toISOString());
            } else if (typeof obj[property] === 'object' && !(obj[property] instanceof File)) {
                objectToFormData(obj[property], fd, formKey);
            } else {
                // if it's a string or a File object
                fd.append(formKey, obj[property]);
            }
        }
    }

    return fd;
};

export const arrayMerge = (array1, array2) => {
    const result_array = [];
    const arr = array1.concat(array2);
    let len = arr.length;
    const assoc = {};

    while (len--) {
        const item = arr[len];

        if (!assoc[item]) {
            result_array.unshift(item);
            assoc[item] = true;
        }
    }

    return result_array;
};

export const guid = () => {
    let d = new Date().getTime();

    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
        d += performance.now(); // use high-precision timer if available
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
};

export const assignExisting = (source, target) => {
    Object.keys(target).forEach((key) => {
        if (key in source) { // or source.hasOwnProperty(key)
            source[key] = target[key];
        }
    });

    return source;
};

// TODO: make this function more reusable, should pass in states you want to remove
// currently removes null, empty and false. Mianly used for cleaning out url params
export const removeNulls = (obj) => {
    const isArray = obj instanceof Array;

    for (const k in obj) {
        if (obj[k] === null || obj[k] === '' || obj[k] === false) isArray ? obj.splice(k, 1) : delete obj[k];
        else if (typeof obj[k] === 'object') removeNulls(obj[k]);
        if (isArray && obj.length === k) removeNulls(obj);
    }

    return obj;
};

export const deepFormat = (obj) => {
    for (const i in obj) {
        if (obj[i] === 'true' || obj[i] === 'false') {
            obj[i] = obj[i] == 'true';
        }
    }

    return obj;
};

/**
 * Returns a depply cloned object without reference.
 * Copied from Vue MultiSelect and Vuex.
 * @type {Object}
 */
const deepClone = function (obj) {
    if (Array.isArray(obj)) {
        return obj.map(deepClone);
    } if (obj && typeof obj === 'object') {
        const cloned = {};
        const keys = Object.keys(obj);
        for (let i = 0, l = keys.length; i < l; i++) {
            const key = keys[i];
            cloned[key] = deepClone(obj[key]);
        }
        return cloned;
    }
    return obj;
};

// example:
// var obj = {foo: {bar: {baz: 'x'}}};
// getPropByString(obj, 'foo.bar.baz');   returns x
export const getPropByString = (obj, propString) => {
    if (!propString) { return obj; }

    let prop; const
        props = propString.split('.');

    for (var i = 0, iLen = props.length - 1; i < iLen; i++) {
        prop = props[i];

        const candidate = obj[prop];
        if (candidate !== undefined) {
            obj = candidate;
        } else {
            break;
        }
    }
    return obj[props[i]];
};

export const replaceAll = (string, search, replacement) => string.replace(new RegExp(search, 'g'), replacement);

export const mapSort = (field) => {
    let direction = 'asc';

    if (field.substr(0, 1) == '-') {
        direction = 'desc';
        field = field.substr(1);
    }

    return {
        direction,
        field,
        sortField: field,
    };
};

// Formats string similar to a page-slug, ie. Website Name! = website-name
export const formatSlugableString = (string) => string.trim().toLowerCase().replace(/ /g, '-').replace(/[^0-9a-z-]/g, '');

export const possibleStringToBool = (val) => (typeof val === 'string' ? val.toLowerCase() === 'true' : val);

export const copyCommand = (value) => {
    const tempInput = document.createElement('input');
    tempInput.style = 'position: absolute; left: -1000px; top: -1000px';
    tempInput.value = value;
    document.body.appendChild(tempInput);
    tempInput.select();
    document.execCommand('copy');
    document.body.removeChild(tempInput);
};

export const dispatchEvent = (name) => {
    if (typeof (Event) === 'function') {
        window.dispatchEvent(new Event(name));
    } else {
        const evt = window.document.createEvent('UIEvents');
        evt.initUIEvent(name, true, false, window, 0);
        window.dispatchEvent(evt);
    }
};

export const poll = (fn, timeout, interval) => {
    const endTime = Number(new Date()) + (timeout || 2000);
    const intervalTime = interval || 100;

    const checkCondition = (resolve, reject) => {
        fn()
        .then((result) => {
            resolve(result);
        }).catch(() => {
            if (Number(new Date()) < endTime) {
                // If the condition isn't met but the timeout hasn't elapsed, poll again.
                setTimeout(checkCondition, intervalTime, resolve, reject);
            } else {
                // Timeout reached and condition is not yet met, reject.
                reject(new Error('Timed out before polling condition met.'));
            }
        });
    };

    return new Promise(checkCondition);
};

export const isEmptyArray = (val) => Array.isArray(val) && val.length === 0;

// https://gist.github.com/hyamamoto/fd435505d29ebfa3d9716fd2be8d42f0#gistcomment-2800233
export const hashCodeFromString = (value) => {
    if (!value) {
        return null;
    }

    return Array.from(value.toString()).reduce((s, c) => Math.imul(31, s) + c.charCodeAt(0) | 0, 0);
};

/**
 * Lighten or darken a hex or rgb color by given percent.
 *
 * @param color
 * @param percent
 * @return {string}
 * @see http://stackoverflow.com/questions/5560248/programmatically-lighten-or-darken-a-hex-color-or-rgb-and-blend-colors
 */
export const shadeColor = (color, percent) => {
    const f = parseInt(color.slice(1), 16);
    const t = percent < 0 ? 0 : 255;
    const p = percent < 0 ? percent * -1 : percent;
    const r = f >> 16;
    const g = f >> 8 & 0x00FF;
    const b = f & 0x0000FF;

    const shadePrimary = (primary) => Math.round((t - primary) * p) + primary;

    const rVal = shadePrimary(r);
    const gVal = shadePrimary(g);
    const bVal = shadePrimary(b);

    return `#${(0x1000000 + rVal * 0x10000 + gVal * 0x100 + bVal).toString(16).slice(1)}`;
};

export const formatUrl = (url) => (/^(f|ht)tps?:\/\//i.test(url) ? url : `http://${url}`);

export const ucFirst = (s) => {
    if (typeof s !== 'string') {
        return s;
    }
    return s.charAt(0).toUpperCase() + s.slice(1);
};

export const downloadHelper = (response, subject, type = null) => {
    let file;
    let fileName = subject;

    if (type == 'pdf') {
        const base = atob(response.replace(/\s/g, ''));
        const len = base.length;
        const buffer = new ArrayBuffer(len);
        const view = new Uint8Array(buffer);
        fileName = subject.includes('.') && subject.slice(-4) != '.pdf' ? (subject + '.pdf') : subject;

        for (let i = 0; i < len; i++) {
            view[i] = base.charCodeAt(i);
        }

        file = new Blob([view], { type: 'application/pdf;base64' });
    } else {
        file = new Blob([response]);
    }

    const windowUrl = window.URL || window.webkitURL;
    const url = windowUrl.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Strip HTML tags from text.
 *
 * Don't use this with HTML from an untrusted source.
 *
 * @param {string} value
 * @return {string}
 * @see https://stackoverflow.com/questions/822452/strip-html-from-text-javascript
 */
export const stripHtml = (value) => {
    const doc = new DOMParser().parseFromString(value, 'text/html');
    return doc.body.textContent || '';
};

/**
 * Check if two elements are overlapping each other.
 */
export const hasOverlap = (topOne, leftOne, widthOne, heightOne, topTwo, leftTwo, widthTwo, heightTwo) => {
    const leftTop = leftTwo > leftOne &&
        leftTwo < (leftOne + widthOne) &&
        topTwo > topOne &&
        topTwo < (topOne + heightOne);

    if (leftTop) {
        return true;
    }

    const rightTop = (leftTwo + widthTwo) > leftOne &&
        (leftTwo + widthTwo) < (leftOne + widthOne) &&
        topTwo > topOne && topTwo < (topOne + heightOne);

    if (rightTop) {
        return true;
    }

    const leftBottom = leftTwo > leftOne &&
        leftTwo < (leftOne + widthOne) &&
        (topTwo + heightTwo) > topOne &&
        (topTwo + heightTwo) < (topOne + heightOne);

    if (leftBottom) {
        return true;
    }

    const rightBottom = (leftTwo + widthTwo) > leftOne &&
        (leftTwo + widthTwo) < (leftOne + widthOne) &&
        (topTwo + heightTwo) > topOne &&
        (topTwo + heightTwo) < (topOne + heightOne);

    if (rightBottom) {
        return true;
    }

    return false;
}

export default {
    objectToFormData,
    arrayMerge,
    guid,
    assignExisting,
    removeNulls,
    deepFormat,
    getPropByString,
    replaceAll,
    formatSlugableString
};
